import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getServices } from '@/services/bookingService';
import type { BarberService } from '@/types';
import './ServicesSection.css';

const SERVICE_ICONS = [
  'ri-scissors-line',
  'ri-men-line',
  'ri-brush-line',
  'ri-magic-line',
  'ri-drop-line',
  'ri-star-line',
  'ri-heart-line',
  'ri-award-line',
];

function formatPrice(price: number): string {
  return `$${price.toLocaleString('es-CO')}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

// ── Skeleton card ──────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="skeleton-card">
      <div className="w-8 h-8 skeleton-block mb-6" />
      <div className="h-5 skeleton-block w-2/3 mb-3" />
      <div className="w-6 h-px bg-[#222222] mb-4" />
      <div className="space-y-2 flex-1 mb-8">
        <div className="h-3 skeleton-block-sm w-full" />
        <div className="h-3 skeleton-block-sm w-5/6" />
        <div className="h-3 skeleton-block-sm w-4/6" />
      </div>
      <div className="flex items-center justify-between pt-4 border-t border-[#111111]">
        <div className="h-3 skeleton-block w-16" />
        <div className="h-6 skeleton-block w-20" />
      </div>
    </div>
  );
}

interface ServicesSectionProps {
  onBooking: () => void;
}

export default function ServicesSection({ onBooking }: ServicesSectionProps) {
  const [visible, setVisible] = useState(false);
  const [services, setServices] = useState<BarberService[]>([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef<HTMLElement>(null);
  const navigate = useNavigate();

  // Intersection observer for entrance animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Fetch active services from Supabase
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getServices().then((data) => {
      if (!cancelled) {
        setServices(data);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      ref={ref}
      id="servicios"
      className={`services-section py-24 md:py-32 relative transition-all duration-700 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/8 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="services-section-label mb-4">◆ Servicios ◆</p>
          <h2 className="services-section-title mb-4">
            Nuestros Servicios
          </h2>
          <p className="services-section-subtitle max-w-md mx-auto">
            Cada servicio diseñado para ofrecerte la mejor experiencia de grooming masculino.
          </p>
          <div className="services-divider" />
        </div>

        {/* Grid */}
        <div className="services-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            : services.map((service, idx) => {
                const icon = SERVICE_ICONS[idx % SERVICE_ICONS.length];
                return (
                  <div
                    key={service.id}
                    className={`service-card ${
                      visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                    }`}
                    style={{
                      transitionDelay: `${idx * 80}ms`,
                      transitionDuration: '600ms',
                    }}
                  >
                    {/* Icon */}
                    <div className="service-card-icon">
                      <i className={`${icon} text-2xl`} />
                    </div>

                    {/* Name */}
                    <h3 className="service-card-title">
                      {service.name}
                    </h3>

                    {/* Divider */}
                    <div className="service-card-divider" />

                    {/* Description */}
                    <p className="service-card-description">
                      {service.landing_description ?? service.description ?? ''}
                    </p>

                    {/* Footer */}
                    <div className="service-card-footer">
                      <span className="service-card-duration">
                        <i className="ri-time-line text-xs" />
                        {formatDuration(service.duration)}
                      </span>
                      <span className="service-card-price">
                        {formatPrice(service.price)}
                      </span>
                    </div>
                  </div>
                );
              })}
        </div>

        {/* Empty state */}
        {!loading && services.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-scissors-line services-empty-icon" />
            <p className="services-empty-text">No hay servicios disponibles en este momento.</p>
          </div>
        )}

        {/* CTA */}
        <div
          className={`text-center mt-14 transition-all duration-700 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <button
            onClick={() => navigate('/reservar')}
            className="services-cta cursor-pointer"
          >
            Reservar ahora
          </button>
        </div>
      </div>
    </section>
  );
}